import { api } from "../api/client.js";
import { shell } from "../ui/layout.js";
import { escapeHtml, formatSize, formatDate } from "../ui/dom.js";

interface FileEntry {
  name: string;
  size: number;
  last_modified?: string;
}

const timeOf = (f: FileEntry): number => (f.last_modified ? Date.parse(f.last_modified) : 0);

const iconFor = (name: string): { cls: string; emoji: string } => {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return { cls: "pdf", emoji: "📄" };
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return { cls: "img", emoji: "🖼️" };
  return { cls: "other", emoji: "📎" };
};

// Open a blank tab inside the click gesture, then point it at the signed URL once it
// resolves (avoids popup-blocking and keeps the app in this tab).
function startDownload(name: string, tab: Window | null = window.open("", "_blank")): void {
  api.downloadUrl(name)
    .then(({ url }) => {
      if (tab) tab.location.href = url;
      else window.location.href = url;
    })
    .catch(() => tab?.close());
}

export async function filesPage(root: HTMLElement): Promise<void> {
  const content = await shell(root, "files");
  content.innerHTML = `
    <div class="hero-container">
      <div class="hero-content">
        <div class="hero-tag">Secure File Distribution</div>
        <h1 class="hero-title">SharePoint</h1>
        <p class="hero-subtitle">Securely download your study guides, documents, and resources.</p>
        <div class="hero-stats">
          <div class="stat"><span class="stat-number" id="fileCount">—</span><span class="stat-label">Files Available</span></div>
          <div class="stat"><span class="stat-number" id="totalSize">—</span><span class="stat-label">Total Size</span></div>
        </div>
        <button class="hero-cta" id="btnDownload" disabled>↓ Download Selected</button>
        <div class="count-label" id="countLabel">Pick files on the right — or use each card's download button</div>
      </div>
      <div class="file-panel">
        <div class="file-panel-label">Available Documents</div>
        <div class="browser-toolbar">
          <input type="text" id="searchInput" class="search-input" placeholder="Search files…" autocomplete="off" />
          <select id="sortSelect" class="sort-select">
            <option value="date-desc">Newest first</option>
            <option value="date">Oldest first</option>
            <option value="name">Name (A–Z)</option>
            <option value="name-desc">Name (Z–A)</option>
            <option value="size">Size (small first)</option>
            <option value="size-desc">Size (large first)</option>
          </select>
        </div>
        <div class="file-list" id="fileList"><div class="state-msg">Loading files…</div></div>
      </div>
    </div>`;

  const list = content.querySelector<HTMLElement>("#fileList");
  const searchEl = content.querySelector<HTMLInputElement>("#searchInput");
  const sortEl = content.querySelector<HTMLSelectElement>("#sortSelect");
  const cta = content.querySelector<HTMLButtonElement>("#btnDownload");
  const countLabel = content.querySelector<HTMLElement>("#countLabel");
  if (!list || !searchEl || !sortEl || !cta || !countLabel) return;

  let files: FileEntry[] = [];
  let lockedSet = new Set<string>();
  const selected = new Set<string>();

  const updateCta = (): void => {
    const n = selected.size;
    cta.disabled = n === 0;
    countLabel.innerHTML =
      n === 0
        ? "Pick files on the right — or use each card's download button"
        : `<span>${n}</span> file${n > 1 ? "s" : ""} selected`;
  };

  const render = (): void => {
    const q = searchEl.value.trim().toLowerCase();
    const sort = sortEl.value;
    const items = files
      .filter((f) => f.name.toLowerCase().includes(q))
      .sort((a, b) => {
        switch (sort) {
          case "name":
            return a.name.localeCompare(b.name);
          case "name-desc":
            return b.name.localeCompare(a.name);
          case "size":
            return a.size - b.size;
          case "size-desc":
            return b.size - a.size;
          case "date":
            return timeOf(a) - timeOf(b);
          default:
            return timeOf(b) - timeOf(a); // date-desc — newest first
        }
      });

    if (!items.length) {
      list.innerHTML = `<div class="state-msg">${files.length ? "No files match your search." : "No files available yet."}</div>`;
      return;
    }

    list.innerHTML = "";
    for (const f of items) {
      const icon = iconFor(f.name);
      const safe = escapeHtml(f.name);
      const meta = formatSize(f.size) + (f.last_modified ? ` · ${formatDate(f.last_modified)}` : "");
      const card = document.createElement("div");

      if (lockedSet.has(f.name.toLowerCase())) {
        card.className = "file-item locked";
        card.innerHTML = `
          <div class="file-row"><div class="file-icon ${icon.cls}">${icon.emoji}</div><div class="lock-badge" title="Locked">🔒</div></div>
          <div class="file-name" title="${safe}">${safe}</div>
          <div class="file-meta">${meta}</div>
          <div class="file-locked-label">Locked</div>`;
      } else {
        card.className = "file-item" + (selected.has(f.name) ? " selected" : "");
        card.innerHTML = `
          <div class="file-row"><div class="file-icon ${icon.cls}">${icon.emoji}</div><div class="file-check">✓</div></div>
          <div class="file-name" title="${safe}">${safe}</div>
          <div class="file-meta">${meta}</div>
          <button class="file-dl">↓ Download</button>`;
        card.addEventListener("click", () => {
          if (selected.has(f.name)) {
            selected.delete(f.name);
            card.classList.remove("selected");
          } else {
            selected.add(f.name);
            card.classList.add("selected");
          }
          updateCta();
        });
        card.querySelector<HTMLButtonElement>(".file-dl")?.addEventListener("click", (e) => {
          e.stopPropagation();
          startDownload(f.name);
        });
      }
      list.appendChild(card);
    }
  };

  searchEl.addEventListener("input", render);
  sortEl.addEventListener("change", render);
  cta.addEventListener("click", () => {
    const names = [...selected];
    if (!names.length) return;
    // Open all tabs synchronously within the gesture, then fill each as URLs resolve.
    const tabs = names.map(() => window.open("", "_blank"));
    names.forEach((n, i) => startDownload(n, tabs[i] ?? null));
  });

  try {
    const [loaded, locks] = await Promise.all([api.files(), api.fileLocks()]);
    files = loaded;
    lockedSet = new Set(locks.map((l) => l.toLowerCase()));
    const count = content.querySelector<HTMLElement>("#fileCount");
    const total = content.querySelector<HTMLElement>("#totalSize");
    if (count) count.textContent = String(files.length);
    if (total) total.textContent = formatSize(files.reduce((s, f) => s + f.size, 0));
    render();
  } catch {
    list.innerHTML = `<div class="state-msg">Could not load files. Please refresh.</div>`;
  }
}
